import { useState, useCallback, useEffect, useRef } from 'react'
import { LuLoaderCircle } from 'react-icons/lu'
import AssetPreview from '../AssetPreview'
import { useShow } from '@/contexts/ShowContext'
import { apiFetch } from '@/lib/api'

// `assets` is the list of assets on the current page; it powers prev/next
// navigation so users can page through assets without leaving the preview.
// `page`/`totalPages` describe where that list sits in the paginated set, and
// `onRequestPage(direction)` loads the adjacent page (returning its raw fetch
// payload) so navigation can cross page boundaries. `onNavigate` is called with
// the newly shown asset each time the user moves, so the grid can keep its
// "recently previewed" highlight in sync.
export default function AssetPreviewModal({ onClose, asset, assets, page = 1, totalPages = 1, onRequestPage, onNavigate, onDelete, onCopy, onMove, onSketchEdit, onCaptured, onRenamed, refreshAsset, onRestoreFromArchive, onDestroy }) {
    const initialList = Array.isArray(assets) && assets.length > 0 ? assets : (asset ? [asset] : [])

    const [items, setItems] = useState(initialList)
    const [currentPage, setCurrentPage] = useState(page)
    const [pageCount, setPageCount] = useState(totalPages)
    const [index, setIndex] = useState(() => Math.max(0, initialList.findIndex(a => a._id === asset?._id)))
    const [paging, setPaging] = useState(false)

    const { show } = useShow()

    // Guard against overlapping page loads when the user clicks/holds quickly.
    const loadingRef = useRef(false)

    const current = items[index] || asset
    const hasPrev = index > 0 || currentPage > 1
    const hasNext = index < items.length - 1 || currentPage < pageCount

    const loadPage = useCallback(async (direction) => {
        if (loadingRef.current || !onRequestPage) return
        loadingRef.current = true
        setPaging(true)
        try {
            const data = await onRequestPage(direction)
            const nextAssets = data?.assets || []
            if (nextAssets.length === 0) return
            setItems(nextAssets)
            if (data?.page != null) setCurrentPage(data.page)
            if (data?.totalPages != null) setPageCount(data.totalPages)
            // Landing position: first item when paging forward, last when back.
            const nextIndex = direction > 0 ? 0 : nextAssets.length - 1
            setIndex(nextIndex)
            onNavigate?.(nextAssets[nextIndex])
        } finally {
            loadingRef.current = false
            setPaging(false)
        }
    }, [onRequestPage, onNavigate])

    const handlePrev = useCallback(() => {
        if (index > 0) {
            const nextIndex = index - 1
            setIndex(nextIndex)
            onNavigate?.(items[nextIndex])
        } else if (currentPage > 1) {
            loadPage(-1)
        }
    }, [index, items, currentPage, onNavigate, loadPage])

    // After an inline rename, re-fetch the asset from the server and replace it
    // in the list with the authoritative data (rather than patching the name
    // locally), then let the parent refresh its own grid. Awaited by the caller
    // so its save spinner stays up until the FE is resynced.
    const handleRenamed = useCallback(async (assetId) => {
        if (show?._id && assetId) {
            try {
                const res = await apiFetch(`/api/shows/${show._id}/assets/${assetId}`)
                if (res.ok) {
                    const fresh = await res.json()
                    setItems(prev => prev.map(a => a._id === fresh._id ? fresh : a))
                }
            } catch (error) {
                console.error('Error resyncing renamed asset:', error)
            }
        }
        await onRenamed?.(assetId)
    }, [show?._id, onRenamed])

    const handleAssetRefresh = useCallback(async (assetId) => {
        const fresh = await refreshAsset?.(assetId)
        if (fresh?._id) setItems(prev => prev.map(a => a._id === fresh._id ? fresh : a))
    }, [refreshAsset])

    const handleNext = useCallback(() => {
        if (index < items.length - 1) {
            const nextIndex = index + 1
            setIndex(nextIndex)
            onNavigate?.(items[nextIndex])
        } else if (currentPage < pageCount) {
            loadPage(1)
        }
    }, [index, items, currentPage, pageCount, onNavigate, loadPage])

    // Arrow-key navigation, ignored while typing in an input/textarea (e.g. the
    // comments box) so it doesn't hijack caret movement.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            const el = document.activeElement
            const tag = el?.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return
            if (e.key === 'ArrowLeft') handlePrev()
            else handleNext()
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [handlePrev, handleNext])

    const positionLabel = (() => {
        if (items.length <= 1 && pageCount <= 1) return null
        const base = `${index + 1} / ${items.length}`
        return pageCount > 1 ? `${base} · Page ${currentPage}/${pageCount}` : base
    })()

    return (
        <div className='relative w-full h-full bg-background border rounded-lg overflow-hidden flex flex-col'>
            <div className='flex-1 min-h-0'>
                <AssetPreview
                    asset={current}
                    onClose={onClose}
                    onPrev={hasPrev ? handlePrev : undefined}
                    onNext={hasNext ? handleNext : undefined}
                    positionLabel={positionLabel}
                    onDelete={onDelete ? async (a) => { await onDelete(a); onClose(null) } : undefined}
                    onCopy={onCopy ? async (a) => { await onCopy(a); onClose(null) } : undefined}
                    onMove={onMove ? async (a) => { onClose(null); await onMove(a) } : undefined}
                    onSketchEdit={onSketchEdit ? (a) => { onClose(null); onSketchEdit(a) } : undefined}
                    onCaptured={onCaptured}
                    onRenamed={handleRenamed}
                    onRefreshAsset={handleAssetRefresh}
                    onRestoreFromArchive={onRestoreFromArchive ? async (ids) => { await onRestoreFromArchive(ids); onClose(null) } : undefined}
                    onDestroy={onDestroy ? async (a) => { await onDestroy(a); onClose(null) } : undefined}
                />
            </div>

            {paging && (
                <div className='absolute inset-0 z-20 flex items-center justify-center bg-background/40 backdrop-blur-sm'>
                    <LuLoaderCircle className='w-6 h-6 animate-spin text-primary' />
                </div>
            )}
        </div>
    )
}
